import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSkillApi,
  deleteSkillApi,
  getSkillApi,
  listSkillsApi,
  listTagsApi,
  parseSkillZipApi,
  updateSkillApi,
  type CreateSkillInput,
  type ListSkillsParams,
  type UpdateSkillInput,
} from './api';

export function useSkillsList(params: ListSkillsParams) {
  return useQuery({
    queryKey: ['skills', params],
    queryFn: () => listSkillsApi(params),
  });
}

export function useSkill(id: string | undefined) {
  return useQuery({
    queryKey: ['skill', id],
    queryFn: () => getSkillApi(id!),
    enabled: !!id,
  });
}

export function useTagsAutocomplete(prefix: string | undefined) {
  return useQuery({
    queryKey: ['tags', prefix ?? ''],
    queryFn: () => listTagsApi(prefix),
    staleTime: 60_000,
  });
}

export function useParseSkillZip() {
  return useMutation({ mutationFn: (file: File) => parseSkillZipApi(file) });
}

export function useCreateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSkillInput) => createSkillApi(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['skills'] });
    },
  });
}

export function useUpdateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateSkillInput) => updateSkillApi(input),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['skills'] });
      qc.setQueryData(['skill', data.id], data);
    },
  });
}

export function useDeleteSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSkillApi(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['skills'] });
    },
  });
}
